import React, { useCallback, useEffect, useState } from "react";
import { Users, UserPlus, Trash2, Copy, ShieldCheck, KeyRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import PasswordGenerator from "@/components/PasswordGenerator";
import ResetPasswordDialog from "@/components/ResetPasswordDialog";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

export default function MembersManager() {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [saving, setSaving] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [resetTarget, setResetTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/members");
      setMembers(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load members");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setForm({ name: "", email: "", password: "" }); setAddOpen(true); };

  const createMember = async (e) => {
    e?.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Name and email are required");
      return;
    }
    if (form.password.length < 8) {
      toast.error("Temporary password must be at least 8 characters");
      return;
    }
    setSaving(true);
    try {
      await api.post("/admin/members", {
        name: form.name.trim(),
        email: form.email.trim(),
        password: form.password,
      });
      toast.success(`${form.name.trim()} added — share their temporary password securely`);
      setAddOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not add member");
    } finally {
      setSaving(false);
    }
  };

  const confirmRemove = async () => {
    if (!removeTarget) return;
    try {
      await api.delete(`/admin/members/${removeTarget.id}`);
      toast.success(`${removeTarget.name || removeTarget.email} removed`);
      setMembers((arr) => arr.filter((m) => m.id !== removeTarget.id));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not remove member");
    } finally {
      setRemoveTarget(null);
    }
  };

  const copyPassword = async () => {
    if (!form.password) return;
    try {
      await navigator.clipboard.writeText(form.password);
      toast.success("Temporary password copied");
    } catch {
      toast.error("Unable to copy");
    }
  };

  const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleDateString(); } catch { return "—"; }
  };

  return (
    <div className="vault-card p-6" data-testid="members-manager">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-[Outfit] text-xl font-medium flex items-center gap-2"><Users size={18} /> Family members</h2>
          <p className="text-sm text-[color:var(--vault-text-secondary)] mt-1">
            Everyone with access to this vault. Add a member directly, or use an invite code below.
          </p>
        </div>
        <Button
          onClick={openAdd}
          className="rounded-xl bg-[color:var(--vault-brand)] hover:bg-[color:var(--vault-brand-hover)] text-white"
          data-testid="add-member-btn"
        >
          <UserPlus size={16} className="mr-1.5" /> Add member
        </Button>
      </div>

      <div className="mt-4 space-y-2">
        {loading ? (
          <div className="text-sm text-[color:var(--vault-text-tertiary)] py-4">Loading members…</div>
        ) : (
          members.map((m) => {
            const isOwner = m.role === "owner" || m.role === "admin";
            const isSelf = m.id === user?.id;
            return (
              <div
                key={m.id}
                className="flex items-center gap-3 rounded-xl bg-[#F1F5FA] border border-[color:var(--vault-border)] px-3 py-2.5"
                data-testid={`member-row-${m.email}`}
              >
                <div className="w-9 h-9 rounded-full flex items-center justify-center text-white shrink-0" style={{ background: isOwner ? "var(--vault-brand)" : "#94A3B8" }}>
                  {isOwner ? <ShieldCheck size={16} /> : <Users size={16} />}
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">
                    {m.name || m.email}{isSelf && <span className="text-[color:var(--vault-text-tertiary)] font-normal"> (you)</span>}
                  </div>
                  <div className="text-xs text-[color:var(--vault-text-tertiary)] truncate">{m.email} · joined {fmtDate(m.created_at)}</div>
                </div>
                <span className="ml-auto pill capitalize shrink-0">{m.role}</span>
                {m.must_change_password && (
                  <span className="pill shrink-0 !bg-[color:var(--vault-warning-bg)] !text-[color:var(--vault-warning)]" data-testid={`member-temp-pw-${m.email}`}>Temp password</span>
                )}
                {!isOwner && !isSelf && (
                  <button
                    onClick={() => setResetTarget(m)}
                    title="Reset password"
                    className="p-2 rounded-lg text-[color:var(--vault-brand)] hover:bg-[color:var(--vault-brand-light)]"
                    data-testid={`reset-member-password-${m.email}`}
                  >
                    <KeyRound size={15} />
                  </button>
                )}
                {!isOwner && !isSelf && (
                  <button
                    onClick={() => setRemoveTarget(m)}
                    title="Remove member"
                    className="p-2 rounded-lg text-[color:var(--vault-error)] hover:bg-[color:var(--vault-error-bg)]"
                    data-testid={`remove-member-${m.email}`}
                  >
                    <Trash2 size={15} />
                  </button>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Add member dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-md bg-white rounded-2xl" data-testid="add-member-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Outfit] text-xl">Add a family member</DialogTitle>
            <DialogDescription>
              Create their account and share the temporary password securely. They can change it after signing in.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={createMember} className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-[0.05em] text-[color:var(--vault-text-tertiary)]">Name</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Sarah"
                className="rounded-xl bg-[#F1F5FA] border-[color:var(--vault-border)] mt-1"
                data-testid="member-name-input"
                autoFocus
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.05em] text-[color:var(--vault-text-tertiary)]">Email</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                placeholder="sarah@example.com"
                className="rounded-xl bg-[#F1F5FA] border-[color:var(--vault-border)] mt-1"
                data-testid="member-email-input"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.05em] text-[color:var(--vault-text-tertiary)]">Temporary password</Label>
              <div className="relative mt-1">
                <Input
                  value={form.password}
                  onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                  placeholder="At least 8 characters"
                  className="rounded-xl bg-[#F1F5FA] border-[color:var(--vault-border)] pr-10 font-mono"
                  data-testid="member-password-input"
                />
                {form.password && (
                  <button
                    type="button"
                    onClick={copyPassword}
                    title="Copy password"
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-[color:var(--vault-text-tertiary)] hover:text-[color:var(--vault-brand)]"
                    data-testid="copy-member-password-btn"
                  >
                    <Copy size={15} />
                  </button>
                )}
              </div>
              <div className="mt-2 flex justify-end">
                <PasswordGenerator onUse={(pw) => setForm((f) => ({ ...f, password: pw }))} triggerLabel="Generate" />
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" className="rounded-xl" onClick={() => setAddOpen(false)} data-testid="member-cancel-btn">Cancel</Button>
              <Button type="submit" disabled={saving} className="rounded-xl bg-[color:var(--vault-brand)] hover:bg-[color:var(--vault-brand-hover)] text-white" data-testid="member-save-btn">
                {saving ? "Adding…" : "Add member"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ResetPasswordDialog member={resetTarget} onClose={() => { setResetTarget(null); load(); }} />

      {/* Remove confirm */}
      <AlertDialog open={!!removeTarget} onOpenChange={(o) => !o && setRemoveTarget(null)}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {removeTarget?.name || removeTarget?.email}?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes their account and everything in their vault, along with any items they shared or emergency access they had. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl" data-testid="remove-member-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmRemove} className="rounded-xl bg-[color:var(--vault-error)] hover:bg-[#b91c1c] text-white" data-testid="remove-member-confirm">
              Remove member
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
